import moment from "moment";
import { useMemo, useState } from "react";
import { SitesContext } from "@/shared/context/Sites";
import type { Cage } from "@/shared/context/sites.graphql";
import { useContextSelector } from "use-context-selector";
import { FallowedSiteWithYear, MappedSite } from "../../toolbar";
import { mapSiteCagesByGroup } from "../SiteCagePicker";

export const useSitePickerData = () => {
  const { loading, sites, selectedSites } = useContextSelector(
    SitesContext,
    (sites) => ({
      loading: sites.loading,
      sites: sites.sites,
      selectedSites: sites.selectedSites,
    }),
  );
  const [showList, setShowList] = useState(false);
  const [search, setSearch] = useState<string>();

  const sortedAndFilteredSites: {
    activeSites: MappedSite[];
    fallowedSites: FallowedSiteWithYear[];
  } = useMemo(() => {
    const lowerCaseSearch = search?.toLowerCase();

    const { activeSites, fallowedSites: deactivatedSites } =
      getActiveAndDeactivatedSites(
        sortAndFilterSites(
          mapSiteCagesByGroup(sites),
          selectedSites,
          lowerCaseSearch,
        ),
      );

    return {
      activeSites: activeSites,
      fallowedSites: deactivatedSites,
    };
  }, [selectedSites, sites, search]);

  return {
    loading,
    showList,
    setShowList,
    siteSearch: search,
    setSiteSearch: setSearch,
    sites: sortedAndFilteredSites,
  };
};

const getActiveAndDeactivatedSites = (sites: MappedSite[]) => {
  return sites.reduce(
    (acc, site) => {
      const isFallowed = site.cages.every((cage) => cage.fallowedSite);

      if (isFallowed) {
        const year = moment(site.createdAt).format("YYYY").toString();

        return {
          ...acc,
          fallowedSites: [...acc.fallowedSites, { ...site, year }],
        };
      } else {
        return {
          ...acc,
          activeSites: [...acc.activeSites, site],
        };
      }
    },
    { activeSites: [], fallowedSites: [] } as {
      activeSites: MappedSite[];
      fallowedSites: FallowedSiteWithYear[];
    },
  );
};

const sortAndFilterSites = (
  sites: MappedSite[],
  selectedSites: number[],
  search: string | undefined,
) => {
  //Because sort mutates the original array, we need to create a copy of the sites array
  return structuredClone(sites)
    .sort((siteA, siteB) => {
      if (selectedSites.includes(siteA.id)) {
        return -1;
      }
      if (selectedSites.includes(siteB.id)) {
        return 1;
      }
      return 0;
    })
    .filter((s) => {
      const lowerCaseName = s.name.toLowerCase();
      return (
        !search ||
        lowerCaseName.toLowerCase().startsWith(search) ||
        lowerCaseName.includes(search)
      );
    })
    .map((s) => ({ ...s, cages: sortCages(s.cages) }));
};

const sortCages = (cages: Cage[]) => {
  return structuredClone(cages).sort(
    (a, b) => Number(a.cageId) - Number(b.cageId),
  );
};
